import type { Execution, PhaseResult } from '../api/generated';

/** "under 1 ms", "12 ms", "1.5 s", "10.0 s". */
export function formatDuration(ms: number): string {
  if (ms < 1) {
    return 'under 1 ms';
  }
  return ms < 1000 ? `${ms} ms` : `${(ms / 1000).toFixed(1)} s`;
}

/** How one phase ended, e.g. "exited 0 in 12 ms" or "timed out after 10.0 s". */
export function describePhase(phase: PhaseResult): string {
  const duration = formatDuration(phase.durationMs);
  switch (phase.outcome) {
    case 'timed_out':
      return `timed out after ${duration}`;
    case 'out_of_memory':
      return `killed: out of memory after ${duration}`;
    case 'exited':
      return `exited ${phase.exitCode ?? '?'} in ${duration}`;
  }
}

/** Where the execution stands, in words. */
export function describeStatus(execution: Execution): string {
  switch (execution.status) {
    case 'queued':
      return 'waiting for a free slot…';
    case 'building':
      return 'building…';
    case 'running':
      return 'running…';
    case 'completed':
      return execution.run === undefined ? 'completed' : describePhase(execution.run);
    case 'build_failed':
      return 'build failed';
    case 'failed':
      return 'the execution infrastructure failed; this paste cannot be run again';
    case 'interrupted':
      return 'interrupted: the service restarted during the run';
  }
}

export function isFinished(execution: Execution | undefined): boolean {
  return execution !== undefined && !['queued', 'building', 'running'].includes(execution.status);
}

/** True when the build left something worth showing: it failed, or the compiler said something. */
export function hasBuildOutput(execution: Execution | undefined): boolean {
  const build = execution?.build;
  return execution?.status === 'build_failed' || (build !== undefined && (build.stdout.text !== '' || build.stderr.text !== ''));
}

// Terminal control sequences (colours, cursor movement, titles) are dropped: output is shown as text.
// eslint-disable-next-line no-control-regex
const CONTROL_SEQUENCES = /\u001b(?:\[[0-9;?]*[ -/]*[@-~]|\][^\u0007\u001b]*(?:\u0007|\u001b\\)|[@-Z\\-_])/g;

/** Program output without terminal control sequences. */
export function plainText(output: string): string {
  return output.replace(CONTROL_SEQUENCES, '');
}
